/**
 * useDeleteCategory Hook
 *
 * Deletes (soft-deletes/hides) a category using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import { extractGraphQLErrorMessage } from "@/lib/graphql-utils";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const DeleteCategoryDocument = `
  mutation DeleteCategory($id: ID!) {
    deleteCategory(id: $id)
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface UseDeleteCategoryResult {
  deleteCategory: (id: string) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Delete (hide) a category.
 */
export function useDeleteCategory(): UseDeleteCategoryResult {
  const [result, executeMutation] = useMutation<
    { deleteCategory: boolean },
    { id: string }
  >(DeleteCategoryDocument);

  const deleteCategory = useCallback(
    async (id: string): Promise<void> => {
      try {
        const response = await executeMutation({ id });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Failed to delete category");
          throw new Error(message);
        }

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Category deleted",
          data: { categoryId: id },
        });
      } catch (err) {
        const deleteError =
          err instanceof Error ? err : new Error("Failed to delete category");

        errorTracking.captureError(deleteError, {
          tags: { feature: "admin", action: "deleteCategory" },
        });

        throw deleteError;
      }
    },
    [executeMutation]
  );

  return {
    deleteCategory,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
